import React from 'react';
import type { HeroData, Page, Component } from '../ComponentEditModal.types';
import { BackgroundImageEditor } from '../shared/BackgroundImageEditor';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';

interface HeroEditorProps {
  formData: HeroData;
  setFormData: (data: HeroData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (mediaType: 'image', assetType: 'background') => void;
  currentPageComponents?: Component[];
}

export const HeroEditor: React.FC<HeroEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();
  const colorFields = getColorFieldsForComponent('hero', themeColors);

  const handleButtonsChange = (buttons: ButtonData[]) => {
    setFormData({ ...data, buttons: buttons as HeroData['buttons'] });
  };

  const handleBackgroundImageChange = (backgroundImage: any) => {
    setFormData({ ...data, backgroundImage });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: {
        ...data.customColors,
        [key]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Tagline */}
      <TextAreaField
        label="Tagline (optional)"
        value={data.tagline || ''}
        onChange={value => setFormData({ ...data, tagline: value })}
        rows={2}
        placeholder="Enter tagline (press Enter for line break)"
        hint="Press Enter to add a line break"
      />

      {/* Title */}
      <TextAreaField
        label="Title *"
        value={data.title || ''}
        onChange={value => setFormData({ ...data, title: value })}
        rows={2}
        placeholder="Enter main title (press Enter for line break)"
        required
        hint="Press Enter to add a line break"
      />

      {/* Subtitle */}
      <TextAreaField
        label="Subtitle (optional)"
        value={data.subtitle || ''}
        onChange={value => setFormData({ ...data, subtitle: value })}
        rows={3}
        placeholder="Enter subtitle or description (press Enter for line break)"
        hint="Press Enter to add a line break"
      />

      {/* Alignment and Size */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectField
          label="Alignment"
          value={data.alignment || 'center'}
          onChange={value =>
            setFormData({ ...data, alignment: value as 'left' | 'center' | 'right' })
          }
          options={[
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
          ]}
        />

        <SelectField
          label="Size"
          value={data.size || 'large'}
          onChange={value => setFormData({ ...data, size: value as 'small' | 'medium' | 'large' })}
          options={[
            { value: 'small', label: 'Small' },
            { value: 'medium', label: 'Medium' },
            { value: 'large', label: 'Large' },
          ]}
        />
      </div>

      {/* Buttons */}
      <ButtonsEditor
        buttons={(data.buttons || []) as ButtonData[]}
        onChange={handleButtonsChange}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
      />

      {/* Color Customization Section */}
      <ColorCustomizationPanel
        colorFields={colorFields}
        customColors={data.customColors || {}}
        onUpdateColor={updateCustomColor}
      />

      {/* Background Image */}
      <div className="border-t pt-6">
        <h3 className="text-md font-medium text-gray-900 mb-4">Background Image</h3>
        <BackgroundImageEditor
          backgroundImage={data.backgroundImage}
          onChange={handleBackgroundImageChange}
          onOpenMediaUpload={() => onOpenMediaUpload('image', 'background')}
        />
      </div>
    </div>
  );
};

// Shared form field components

interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  hint?: string;
}

const TextAreaField: React.FC<TextAreaFieldProps> = ({
  label,
  value,
  onChange,
  rows = 2,
  placeholder,
  required,
  hint,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      rows={rows}
      placeholder={placeholder}
      required={required}
    />
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

const SelectField: React.FC<SelectFieldProps> = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);
